<template>
  <WorkflowReviewStatus
    v-if="shouldRenderContent"
    :item-class="itemClass"
    :item-id="itemId"
    :ledger-id="ledgerId"
    :should-render-inline="shouldRenderInline"
    :review="effectiveReview"
    :reviews="effectiveReviews"
    :preparation="effectivePreparation"
    :parent-id="itemId"
    :tracking-prefix="'template-screen'"
    @item-review-update="handleItemReviewUpdate"
    @item-preparation-update="handleItemPreparationUpdate"
  />
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'

import { SfLog } from '@app/adapters/logger'
import WorkflowReviewStatus from '@app/application/workflows/screens/workflow-review/components/workflow-review-status.vue'
import type {
  TemplatePreparation,
  TemplateReview,
} from '@app/application/workflows/types'
import type { DataAlterationGuardRequest } from '@app/shared/services/data-alteration-guard.types'
import {
  publishGuard,
  withdrawGuard,
} from '@app/shared/services/data-alteration-guard'
import SFetch from '@app/utils/sfetch'

interface Props {
  itemClass: 'account_entry' | 'reconciliation_text' | undefined
  itemId: number
  ledgerId: number
  review?: TemplateReview | null
  reviews: Array<{
    name: string
    time: string
    outdatedAt: Date | null
  }>
  preparation?: TemplatePreparation | null
  shouldRenderContent?: boolean
  shouldRenderInline?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  review: null,
  preparation: null,
  shouldRenderContent: true,
  shouldRenderInline: false,
  reviews: () => [],
})

const effectiveReviews = ref(props.reviews)
const effectiveReview = ref(props.review)
const effectivePreparation = ref(props.preparation)

// The write outdates the reviews on the server, and this screen only learns
// that when the refetch returns. A second edit inside that window would ask the
// user to remove reviews that are already gone.
const reviewsOutdatedByOwnWrite = ref(false)

const hasAnyUpToDateReview = computed(() => {
  return (
    effectiveReviews.value &&
    effectiveReviews.value.some((review) => review.outdatedAt === null)
  )
})

const reviewers = computed(() => {
  return effectiveReviews.value.map((review) => review.name).join(', ')
})

onMounted(() => {
  publishGuard('unreview-confirmation')
  window.eventHub.$on(
    'data-alteration-guard-request',
    decideDataAlterationGuard
  )
  window.eventHub.$on('reloadWorkflowReviewStatusData', reloadData)
})

onBeforeUnmount(() => {
  withdrawGuard('unreview-confirmation')
  window.eventHub.$off(
    'data-alteration-guard-request',
    decideDataAlterationGuard
  )
  window.eventHub.$off('reloadWorkflowReviewStatusData', reloadData)
})

function handleItemReviewUpdate(
  {
    review,
    reviews,
  }: { review: TemplateReview | null; reviews: Props['reviews'] } = {
    review: null,
    reviews: [],
  }
) {
  reviewsOutdatedByOwnWrite.value = false
  effectiveReviews.value = reviews
  effectiveReview.value = review
}

function handleItemPreparationUpdate(
  { preparation }: { preparation: TemplatePreparation | null } = {
    preparation: null,
  }
) {
  effectivePreparation.value = preparation
}

// The decider for the unreview-confirmation guard. A template with no up-to-date
// review needs no confirmation, so the request passes straight through and the
// caller never sees a modal.
function decideDataAlterationGuard(request: DataAlterationGuardRequest) {
  if (request.guard !== 'unreview-confirmation') return

  const { requestId, guard, options } = request

  if (!hasAnyUpToDateReview.value || reviewsOutdatedByOwnWrite.value) {
    window.eventHub.$emit('data-alteration-guard-result', {
      requestId,
      guard,
      confirmed: true,
      skipUnprepare: false,
    })
    return
  }

  window.modalCitizen.openModal(
    'confirm-unreview-template-modal',
    {
      requestId,
      reviewers: reviewers.value,
      preparer: effectivePreparation.value?.name,
      keepOpenAfterConfirmation: options?.keepOpenAfterConfirmation,
    },
    {
      autoClose: false,
    }
  )
}

async function reloadData(eventData?: { skipUnprepare?: boolean } | undefined) {
  // This functionality is not used when the itemClass is undefined.
  if (!props.itemClass) return

  // Reloading data is currently only used to change the review status
  // from reviewed to unreviewed. It makes no sense to reload the data when
  // the template is already unreviewed.
  if (!hasAnyUpToDateReview.value) return

  if (!eventData?.skipUnprepare) {
    handleItemPreparationUpdate({ preparation: null })
  }

  reviewsOutdatedByOwnWrite.value = true

  const url = window.Silverfin.Routes.ledger_reconciliation_reviews_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    props.ledgerId,
    props.itemClass,
    props.itemId
  )

  try {
    const data = (await SFetch.jsonWithAlert(url)) as {
      review: TemplateReview | null
      reviews: Props['reviews']
    }
    const { review, reviews } = data
    handleItemReviewUpdate({ review, reviews })
  } catch (e) {
    // The refetch failed, so this screen no longer knows the review state.
    // Asking one time too many is harmless. Skipping the modal is not.
    reviewsOutdatedByOwnWrite.value = false

    SfLog.warn('Error during reloading the workflow review status data', {
      extra: {
        error: e,
      },
    })
  }
}
</script>
